import { Modal } from "antd";
import _, { isEmpty } from "lodash";
import { FC, Fragment, useEffect, useState } from "react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { getDDMMYYYFormat, getHHmmFormat } from "../../../helper/dateTimeHelper";
import moduleConfig from "../../../module.config";
import { IInputProps, IUseFormStructureItem } from "../../../modules/form";
import { ClassNames, ObjectUtils } from "../../../modules/utils";
import { FileAttachmentCard } from "../../../pages/procedure/upload";
import FontFa from "../../fontFa/fontFa";
import { Icon } from "../../icon";
import { ImagePreview } from "../../image-preview";
import { CreateNotification } from "../../notifier";
import DownloadExcelTemplate from "./DownloadExcelTemplate";
import DownloadPassport from "./DownloadPassport";
import UploadFile from "./UploadFile";
import type { ExcelTemplateType } from "./excel-template";
import { InvalidDataType, VALIDATE_IMPORT_FILE_TYPE } from "./excel-template/template/_utils";

export interface IInputArrayDataStructureItem extends IUseFormStructureItem {
  isVisibleOnList?: boolean;
  render?: (rowValue: any) => any;
}

export interface IInputArrayFormProps {
  childFormData: any;
  structure: IInputArrayDataStructureItem[];
  teamList?: string[];
  isOnlyPassport?: boolean;
  isRefund?: boolean;
  isSelectUnit?: number;
  onSubmit: (values: any) => void;
  name: string;
  isUpdateUI: boolean;
  unit?: string;
  phoneNumber?: string;
  // ============================ Events ============================
  onRemove?: (id: any, values: any) => Promise<any>;
  onUpdate?: (id: any, values: any) => Promise<any>;
}

export interface IInputArrayDataProps extends IInputProps {
  isDisableEdit?: boolean;
  generation?: number;
  form: FC<IInputArrayFormProps>;
  structure: IInputArrayDataStructureItem[];
  teamList?: string[];
  isOnlyPassport?: boolean;
  isRefund?: boolean;
  isTrip?: boolean;
  isSelectUnit?: number;
  notifyText?: string;
  isDisabled?: boolean;
  excelTemplate?: ExcelTemplateType;
  exportPassport?: boolean;
  unit?: string;
  phoneNumber?: string;
  // ============================ Events ============================
  onCreate?: (values: any) => Promise<any>;
  onRemove?: (id: any, values: any) => Promise<any>;
  onUpdate?: (id: any, values: any) => Promise<any>;
}

export const InputArrayData: FC<IInputArrayDataProps> = (props) => {
  const [disabledUpload, setDisabledUpload] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formData, setFormData] = useState(null) as any;
  const data = Array.isArray(props.value)
    ? props.value.map((item, index) => ({ ...item, id: item.id || index + 1 }))
    : [];
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectBox, setIsSelectBox] = useState(false);
  const [checkedList, setCheckedList] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const childStructureVisible = props.structure.filter((v) => v.isVisibleOnList || v.render);
  const childFormId = `InputArrayData__Form__${props.name}`;
  const isUpdateUI = !!formData;

  useEffect(() => {
    if (isFormVisible) {
      document.onkeyup = function (e) {
        if (e.key === "Escape") closeForm();
      };
    }
  }, [isFormVisible]);

  const closeForm = () => {
    setIsFormVisible(false);
    setFormData(null);
  };

  const isImageFile = (str: string) =>
    typeof str === "string" && str?.includes("https://viagsstorage.blob.core.windows.net/viags-file/ImageSmall");

  const isDocumentFile = (fileUrl: string) =>
    typeof fileUrl === "string" && fileUrl?.includes("https://viagsstorage.blob.core.windows.net/viags-file/file");

  const isGrantedArea = (listArea: any[]) => {
    return Array.isArray(listArea);
  };

  const isImageArray = (imagesUrl: string[]) => {
    return Array.isArray(imagesUrl) && imagesUrl.every((s) => isImageFile(s));
  };

  const copyItem = (value: any) => {
    const newData = _.cloneDeep(data);
    const newValue = { ...value, id: data.length + 2 };
    newData.push(newValue);
    props.onChange(newData);
  };

  const removeItem = async (id: any, values: any) => {
    setIsLoading(true);
    try {
      if (props.onRemove) await props.onRemove(id, values);
      const newData = data.filter((v) => v.id !== id);
      props.onChange(newData);
      setDisabledUpload(!isEmpty(newData));
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const isChecked = (childValue: any) => {
    return checkedList.findIndex((e: any) => e.id === childValue.id) >= 0;
  };

  const handleHeaderCheckboxChange = (event: any) => {
    const isChecked = event.target.checked;

    setCheckedList(isChecked ? [...data] : []);
  };

  const onCheckboxChange = (event: any, childValue: any) => {
    const isChecked = event.target.checked;
    const index = checkedList.findIndex((e: any) => e.id === childValue.id);

    if (index >= 0) {
      if (!isChecked) {
        setCheckedList((checkedList) => {
          const newCheckList = [...checkedList];
          newCheckList.splice(index, 1);
          return newCheckList;
        });
      }
    } else {
      setCheckedList((checkedList) => [...checkedList, childValue]);
    }
  };

  const handleDeleteSelection = () => {
    const newCheckList = _.differenceWith(data, checkedList, _.isEqual);
    props.onChange(newCheckList);
    setCheckedList([]);
    setIsSelectBox(false);
    CreateNotification({
      title: "Thành công",
      body: "Xóa các dữ liệu đã chọn thành công!",
      icon: "/icons/success.png",
    });
    return;
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    handleDeleteSelection();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleUploadFile = (data: unknown[]) => {
    props.onChange(data);
  };

  const isInvalidField = (field: any) => {
    return (
      typeof field === "object" &&
      !Array.isArray(field) &&
      field !== null &&
      "type" in field &&
      field.type === VALIDATE_IMPORT_FILE_TYPE
    );
  };

  return (
    <Fragment>
      <div className={ClassNames({ InputArrayData: true, [`generation-${props.generation || 0}`]: true })}>
        {isLoading ? (
          <div className="tableLoading">
            <Icon.Loading />
          </div>
        ) : null}
        <div className="ctas float-left">
          {!props.isDisabled && (
            <>
              <button
                type="button"
                disabled={props.isDisableEdit}
                className="btnAddMore"
                onClick={() => {
                  if (props.isSelectUnit === 0) {
                    CreateNotification({
                      title: "Thông báo",
                      body: props.notifyText || "Anh/Chị vui lòng chọn CQ/ĐV",
                      icon: "/icons/warning.png",
                    });
                    return;
                  }
                  setIsFormVisible(true);
                }}
              >
                Thêm mới
              </button>

              {!isSelectBox && (
                <button
                  type="button"
                  disabled={props.isDisableEdit}
                  className="btnDeleteAll"
                  onClick={() => setIsSelectBox(true)}
                >
                  Chọn nhiều để xóa
                </button>
              )}

              {checkedList.length > 0 && (
                <button type="button" disabled={props.isDisableEdit} className="btnConfirmDelete" onClick={showModal}>
                  Xóa dữ liệu đã chọn
                </button>
              )}

              {checkedList.length <= 0 && isSelectBox && (
                <button
                  type="button"
                  disabled={props.isDisableEdit}
                  className="btnBackDelete"
                  onClick={() => setIsSelectBox(false)}
                >
                  Quay lại
                </button>
              )}

              {props.excelTemplate && (
                <>
                  <UploadFile
                    onChange={handleUploadFile}
                    disabled={disabledUpload}
                    template={props.excelTemplate}
                    onClick={(e) => {
                      if (props.isSelectUnit === 0) {
                        CreateNotification({
                          title: "Thông báo",
                          body: props.notifyText || "Anh/Chị vui lòng chọn CQ/ĐV",
                          icon: "/icons/warning.png",
                        });
                        e.preventDefault();
                      }
                    }}
                  />
                  <DownloadExcelTemplate template={props.excelTemplate} />
                </>
              )}
            </>
          )}
          {props.exportPassport && <DownloadPassport value={props.value} isHideRefund />}
        </div>

        <table>
          <thead>
            {/* <tr>

                {!props.isDisableEdit || !isSelectBox ? 
                    <th className="colActions"></th> :
                    <th className="colActions">
                      <div className="btnRemove">
                        <input className="btnRemove" type="checkbox" onChange={handleHeaderCheckboxChange}/>
                      </div>
                    </th> 
                }
                <th>STT.</th>
                {childStructureVisible.map(({ label }, key) => {
                  return <th key={key}>{label}</th>;
                })}
              </tr> */}
            <tr>
              {!props.isDisableEdit && (
                <>
                  {isSelectBox ? (
                    <th className="colActions">
                      <div className="btnRemove">
                        <input className="btnRemove" type="checkbox" onChange={handleHeaderCheckboxChange} />
                      </div>
                    </th>
                  ) : (
                    <th className="colActions"></th>
                  )}
                </>
              )}
              <th>STT.</th>
              {childStructureVisible.map(({ label }, key) => {
                return <th key={key}>{label}</th>;
              })}
            </tr>
          </thead>

          {data.length > 0 ? (
            <tbody>
              {data.map((childValue, childValueKey) => {
                return (
                  <tr key={childValueKey}>
                    {!props.isDisableEdit && (
                      <>
                        {isSelectBox ? (
                          <td className="colActions">
                            <div className="btnRemove">
                              <input
                                className="btnRemove"
                                type="checkbox"
                                checked={isChecked(childValue)}
                                onChange={(event: any) => onCheckboxChange(event, childValue)}
                              />
                            </div>
                          </td>
                        ) : (
                          <td className="colActions">
                            <div
                              className="btnEdit"
                              onClick={() => {
                                /**
                                 * Remove invalid field
                                 */
                                for (const [key, value] of Object.entries(childValue)) {
                                  if (isInvalidField(value)) {
                                    childValue[key] = "";
                                  }
                                }
                                setFormData(childValue);
                                setIsFormVisible(true);
                              }}
                            >
                              <Icon.Edit />
                            </div>

                            <div
                              className="btnCopy"
                              title="Copy thông tin"
                              hidden={props.isDisabled}
                              onClick={() => copyItem(childValue)}
                            >
                              <FontFa name="faCopy" color="#11EB3F" />
                            </div>

                            <div className="btnRemove" onClick={() => removeItem(childValue.id, childValue)}>
                              <Icon.Remove />
                            </div>
                          </td>
                        )}
                      </>
                    )}

                    <td>{childValueKey + 1}</td>
                    {childStructureVisible.map(({ name, render, isMutilLocale }, key) => {
                      return (
                        <td key={key}>
                          {(() => {
                            if (render) return render(childValue);
                            if (isMutilLocale)
                              return ObjectUtils.getIn(
                                childValue,
                                `${name}.${moduleConfig.getLocaleList()[0].key}`,
                                "",
                              );

                            const obj = ObjectUtils.getIn(childValue, name, "");

                            if (isDocumentFile(obj)) {
                              const listFile: [string] = obj ? JSON.parse(obj) : [];
                              return <FileAttachmentCard listFile={listFile}></FileAttachmentCard>;
                            }
                            if (isImageFile(obj))
                              return (
                                <PhotoProvider>
                                  <PhotoView src={obj}>
                                    <img src={obj} width={100} height={100} alt="" style={{ cursor: "pointer" }}></img>
                                  </PhotoView>
                                </PhotoProvider>
                              );
                            if (isImageArray(obj)) return <ImagePreview imageUrls={obj} />;
                            if (!props.isTrip && isGrantedArea(obj)) {
                              // const grantedArea = childValue?.grantedArea  ? JSON.parse(childValue.grantedArea) : [];
                              const grantedArea = [...childValue?.grantedArea];

                              return (
                                <div className="d-flex" style={{ width: "200%", maxWidth: "120%" }}>
                                  {grantedArea.map((e: any) => (
                                    <div style={{ backgroundColor: e.color, color: "#FFFFFF", padding: "2px" }}>
                                      {e.label}
                                    </div>
                                  ))}
                                </div>
                              );
                            }

                            if (props.isTrip && isGrantedArea(obj)) {
                              const trip: any[] = [...childValue?.TripBusiness];
                              return (
                                <div>
                                  {trip.map((e: any) => (
                                    <div>{`${e.from} - ${e.to} (${getDDMMYYYFormat(e.date)} ${getHHmmFormat(
                                      e.date,
                                    )})`}</div>
                                  ))}
                                </div>
                              );
                            }

                            if (isInvalidField(obj)) {
                              const { isValid, text, message } = obj as InvalidDataType;

                              if (!isValid) {
                                return (
                                  <span style={{ color: "red" }} title={message}>
                                    {text ?? ""}
                                  </span>
                                );
                              }
                            }
                            return `${obj ?? ""}`;
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          ) : null}
        </table>

        {data.length > 0 ? null : (
          <div className="message emptyData">
            <Icon.Data />
            Chưa có dữ liệu
          </div>
        )}
      </div>

      {isFormVisible ? (
        <div
          id={childFormId}
          className={ClassNames({ InputArrayData__Form: true, [`generation-${props.generation || 0}`]: true })}
          // onClick={(e: any) => {
          //     if (e.target.id === childFormId) setIsFormVisible(false);
          // }}
        >
          <div className="wraper">
            <div className="head">
              <div className="title">
                <span onClick={() => closeForm()}>
                  <Icon.ArrowLeft />
                </span>
                {formData ? "Chỉnh sửa" : "Thêm mới"}: {props.label}
              </div>

              <div className="btnClose" onClick={() => closeForm()}>
                <Icon.Close />
              </div>
            </div>

            <div className="body">
              <props.form
                isUpdateUI={isUpdateUI}
                childFormData={formData}
                structure={props.structure}
                teamList={props.teamList}
                isOnlyPassport={props.isOnlyPassport}
                isRefund={props.isRefund}
                onSubmit={async (values) => {
                  if (isUpdateUI) {
                    try {
                      let id = data.length + 1; // Updated this line to generate a unique ID

                      try {
                        if (props.onUpdate) await props.onUpdate(formData.id, values);

                        if (formData.id) {
                          props.onChange(
                            data.map((item) => {
                              if (item.id === formData.id) return { ...formData, ...values };
                              else return item;
                            }),
                          );
                        } else props.onChange([...data, { ...values, id }]);
                      } catch (error) {
                        console.error(error);
                      }
                      setIsFormVisible(false);
                    } catch (error) {
                      console.error(error);
                    }
                  } else {
                    try {
                      let id = data.length + 2;

                      try {
                        if (props.onCreate) id = (await props.onCreate(values)) || Date.now();
                        props.onChange([...data, { ...values, id }]);
                      } catch (error) {}
                      setIsFormVisible(false);
                    } catch (error) {
                      console.error(error);
                    }
                  }
                }}
                name={props.name}
                onRemove={props.onRemove}
                onUpdate={props.onUpdate}
              />
            </div>
          </div>
        </div>
      ) : null}
      <Modal
        title="Xác nhận"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{ danger: true }}
        cancelText="Đóng"
        okText="Xác nhận"
      >
        <p>Anh/Chị xác nhận muốn xóa các dữ liệu đã chọn</p>
      </Modal>
    </Fragment>
  );
};
